import React, { useState, useEffect } from 'react';
import { amplifyApi } from '../../services/amplifyApi';
import { DollarSign, TrendingUp, Wallet, CheckCircle, Clock, Loader2, RefreshCw, AlertCircle } from 'lucide-react';

// $25 minimum payout threshold (in cents)
const PAYOUT_THRESHOLD = 2500;

// Helper to format cents to dollars
const formatCurrency = (cents) => {
  if (cents === undefined || cents === null) return '$0.00';
  return `$${(cents / 100).toFixed(2)}`;
};

// Helper to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

function EarningsTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    loadEarningsData();
  }, []);

  const loadEarningsData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await amplifyApi.getMyCommissions();

      if (response.success !== false) {
        setProfile(response.profile || {});
        setStats(response.stats || {});
        setHistory(response.recentHistory || []);
      } else {
        setError(response.error || 'Failed to load earnings data');
      }
    } catch (err) {
      setError(err.message || 'Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate payout eligibility
  const pendingBalance = profile?.pendingBalance || 0;
  const isEligibleForPayout = pendingBalance >= PAYOUT_THRESHOLD;
  const payoutProgress = Math.min((pendingBalance / PAYOUT_THRESHOLD) * 100, 100);
  const amountNeeded = Math.max(PAYOUT_THRESHOLD - pendingBalance, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-slate-600">Loading earnings...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <p className="text-slate-600 mb-4">{error}</p>
        <button
          onClick={loadEarningsData}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <RefreshCw size={16} />
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Earnings */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <TrendingUp size={16} />
            <span className="text-sm">Total Earnings</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">
            {formatCurrency(profile?.totalEarnings)}
          </p>
        </div>

        {/* Pending */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <Clock size={16} />
            <span className="text-sm">Pending</span>
          </div>
          <p className="text-2xl font-bold text-amber-600">
            {formatCurrency(profile?.pendingBalance)}
          </p>
        </div>

        {/* Paid Out */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <Wallet size={16} />
            <span className="text-sm">Paid Out</span>
          </div>
          <p className="text-2xl font-bold text-green-600">
            {formatCurrency(profile?.paidOutBalance)}
          </p>
        </div>

        {/* Payout Status */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <DollarSign size={16} />
            <span className="text-sm">Payout Status</span>
          </div>
          {isEligibleForPayout ? (
            <div className="flex items-center gap-2">
              <CheckCircle size={20} className="text-green-600" />
              <span className="text-lg font-semibold text-green-600">Eligible</span>
            </div>
          ) : (
            <p className="text-lg font-semibold text-slate-600">
              Need {formatCurrency(amountNeeded)}
            </p>
          )}
        </div>
      </div>

      {/* Payout Progress Banner */}
      {!isEligibleForPayout && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-900">Progress to Payout</span>
            <span className="text-sm text-blue-700">{payoutProgress.toFixed(0)}%</span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${payoutProgress}%` }}
            />
          </div>
          <p className="text-xs text-blue-700 mt-2">
            Earn {formatCurrency(amountNeeded)} more to reach the $25 minimum payout threshold
          </p>
        </div>
      )}

      {/* Ready for Payout Banner */}
      {isEligibleForPayout && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle size={24} className="text-green-600" />
            <div>
              <p className="font-semibold text-green-900">Ready for Payout!</p>
              <p className="text-sm text-green-700">
                You have {formatCurrency(pendingBalance)} available for withdrawal
              </p>
            </div>
          </div>
          <button
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
            disabled
            title="Coming soon"
          >
            Request Payout
          </button>
        </div>
      )}

      {/* Commission History */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Commission History</h3>
          <button
            onClick={loadEarningsData}
            className="text-slate-500 hover:text-slate-700 p-1"
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
        </div>

        {history.length === 0 ? (
          <div className="px-4 py-12 text-center text-slate-500">
            <DollarSign size={40} className="mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No commissions yet</p>
            <p className="text-sm mt-1">
              When your products sell or someone uses your referral link, you'll see your earnings here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Order</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Sale</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Commission</th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {history.map((item, index) => (
                  <tr key={item.ledgerId || index} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-600">
                      {formatDate(item.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          item.type === 'creator'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-purple-100 text-purple-700'
                        }`}
                      >
                        {item.type === 'creator' ? 'Creator' : 'Referral'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">
                      {item.orderId ? `#${item.orderId.substring(0, 8)}...` : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-900">
                      {formatCurrency(item.orderAmount)}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-green-600">
                      +{formatCurrency(item.commissionAmount)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          item.status === 'paid'
                            ? 'bg-green-100 text-green-700'
                            : item.status === 'approved'
                            ? 'bg-blue-100 text-blue-700'
                            : item.status === 'cancelled'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {item.status || 'pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Stats Summary */}
      {stats && (stats.totalTransactions > 0) && (
        <div className="bg-slate-100 rounded-xl p-4">
          <p className="text-sm text-slate-600">
            <span className="font-medium">{stats.totalTransactions}</span> total transactions
            {stats.pendingCount > 0 && (
              <span> · <span className="font-medium">{stats.pendingCount}</span> pending</span>
            )}
            {stats.approvedCount > 0 && (
              <span> · <span className="font-medium">{stats.approvedCount}</span> approved</span>
            )}
            {stats.paidCount > 0 && (
              <span> · <span className="font-medium">{stats.paidCount}</span> paid</span>
            )}
          </p>
        </div>
      )}
    </div>
  );
}

export default EarningsTab;
